import type { FC, JSX, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { CommentCard } from "@/features/study-group-stream/components/CommentCard";
import { ReplyCard } from "@/features/study-group-stream/components/ReplyCard";
import {
  LINK_BUTTON_CLASS,
  REPLY_INDENT_CLASS,
} from "@/features/study-group-stream/utils/commentSectionLayout";
import type { Comment, ReactionType } from "@/lib/humhub-api/types";

export type CommentWithVisibility = Comment & {
  visibleReplies: Comment[];
  hiddenRepliesCount: number;
  areRepliesExpanded: boolean;
};

interface CommentThreadProps {
  comment: CommentWithVisibility;
  replyCtx: { parentId: number; anchorId: number } | null;
  highlightedCommentIds: ReadonlySet<number>;
  onReaction: (
    commentId: number,
    reaction: ReactionType | null,
  ) => Promise<void>;
  onReplyClick: (parentId: number, anchorId: number) => void;
  onDeleteComment: (commentId: number) => Promise<void>;
  onEditComment: (commentId: number, newMessage: string) => Promise<void>;
  onToggleReplies: (commentId: number, areRepliesExpanded: boolean) => void;
  isCurrentUserAuthor: (comment: Comment) => boolean;
  currentUserId?: number;
  topicIds?: Array<string | number>;
  onSendMessage?: (userId: number) => void;
  renderReplyInput: (
    parentId: number,
    anchorId: number,
    variant: "comment" | "reply",
  ) => JSX.Element | null;
  renderHiddenRepliesLabel: (count: number, isExpanded: boolean) => ReactNode;
  isArchived?: boolean;
}

export const CommentThread: FC<CommentThreadProps> = ({
  comment,
  replyCtx,
  highlightedCommentIds,
  onReaction,
  onReplyClick,
  onDeleteComment,
  onEditComment,
  onToggleReplies,
  isCurrentUserAuthor,
  currentUserId,
  topicIds,
  onSendMessage,
  renderReplyInput,
  renderHiddenRepliesLabel,
  isArchived = false,
}) => {
  return (
    <div className="stream-bg-comment mt-1 min-w-0 rounded-tr-xl px-2 py-1">
      <CommentCard
        comment={{ ...comment, comments: undefined }}
        onReaction={onReaction}
        onReplyClick={(anchorId) => onReplyClick(comment.id, anchorId)}
        onDeleteComment={onDeleteComment}
        onEditComment={onEditComment}
        isCurrentUserAuthor={isCurrentUserAuthor(comment)}
        currentUserId={currentUserId}
        onSendMessage={onSendMessage}
        isCommentActive={replyCtx?.anchorId === comment.id}
        isHighlighted={highlightedCommentIds.has(Number(comment.id))}
        disabled={isArchived}
        topicIds={topicIds}
      />

      {!isArchived && renderReplyInput(comment.id, comment.id, "comment")}

      {comment.visibleReplies.map((reply) => (
        <div key={reply.id} className={`mt-1 min-w-0 ${REPLY_INDENT_CLASS}`}>
          <ReplyCard
            comment={reply}
            onReaction={onReaction}
            onReplyClick={() => onReplyClick(comment.id, reply.id)}
            onDeleteComment={onDeleteComment}
            onEditComment={onEditComment}
            isCurrentUserAuthor={isCurrentUserAuthor(reply)}
            currentUserId={currentUserId}
            onSendMessage={onSendMessage}
            isCommentActive={replyCtx?.anchorId === reply.id}
            isHighlighted={highlightedCommentIds.has(Number(reply.id))}
            disabled={isArchived}
          />
          {!isArchived && renderReplyInput(comment.id, reply.id, "reply")}
        </div>
      ))}

      {(comment.hiddenRepliesCount > 0 || comment.areRepliesExpanded) && (
        <div
          className={`mb-2 mt-1 min-w-0 ${REPLY_INDENT_CLASS}`}
          data-reply-toggle-comment-id={comment.id}
        >
          <Button
            variant="ghost"
            size="sm"
            className={LINK_BUTTON_CLASS}
            onClick={() =>
              onToggleReplies(comment.id, comment.areRepliesExpanded)
            }
          >
            {renderHiddenRepliesLabel(
              comment.hiddenRepliesCount,
              comment.areRepliesExpanded,
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
